import React, { useCallback, useMemo, useState } from "react";
import {
  Text,
  Button,
  RadioOption,
  Radio,
  ReactSelect,
  DialogModal,
} from "@pushpress/shared-components";
import { CompanyService } from "@pushpress/committed-club-typescript-sdk";
import {
  getCurrentMonthOption,
  getNextMonths,
  getStartDateFromMonthOption,
} from "./utils";
import { MonthOption, UpdateClubCallback } from "./types";
import { useAnalytics } from "../../components/providers/AnalyticsProvider";
import { CommittedClubEvent } from "./CommittedClubEvents";

type SetupMonthPaneProps = {
  value?: MonthOption;
  ready: boolean;
  isClubSetup: boolean;
  hasLaunched: boolean;
  hasExistingClub: boolean | undefined;
  setHasExistingClub: React.Dispatch<React.SetStateAction<boolean>>;
  updateClub: UpdateClubCallback;
  createClub: (month: MonthOption) => void;
};

function SetupNamePane(props: SetupMonthPaneProps) {
  const {
    value,
    ready,
    hasExistingClub,
    isClubSetup,
    hasLaunched,
    setHasExistingClub,
    createClub,
    updateClub,
  } = props;
  const months = useMemo(getNextMonths, []);

  const [error, setError] = useState<string>();
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [startMonthLocal, setStartMonthLocal] = useState<MonthOption>(
    value || getCurrentMonthOption(),
  );
  const [hasExistingClubLocal, setHasExistingClubLocal] =
    useState(hasExistingClub);
  const [finishedSurvey, setFinishedSurvey] = useState(
    hasExistingClub !== undefined,
  );

  const isThisMonth = startMonthLocal?.value === getCurrentMonthOption().value;

  const { trackEvent } = useAnalytics();

  const toggleSurveyAnswer = useCallback((val: boolean) => {
    setError(undefined);
    setHasExistingClubLocal(val);
  }, []);

  const submitSurvey = useCallback(() => {
    if (hasExistingClubLocal === undefined) {
      return setError("Required");
    }
    CompanyService.updateCompanyCommittedClub({
      body: { ranCommittedClub: hasExistingClubLocal },
    });
    setHasExistingClub(hasExistingClubLocal);
    setFinishedSurvey(true);
  }, [hasExistingClubLocal, setHasExistingClub]);

  const launchClub = useCallback(() => {
    trackEvent(CommittedClubEvent.StartMonthSaved, {
      app: "committed_club",
      live_now: isThisMonth,
      state: hasLaunched ? "launched" : "pre_launch",
      page: "start_month_panel",
    });

    if (isClubSetup) {
      updateClub({ startDate: getStartDateFromMonthOption(startMonthLocal) });
    } else {
      createClub(startMonthLocal);
    }
  }, [
    isThisMonth,
    hasLaunched,
    isClubSetup,
    startMonthLocal,
    createClub,
    updateClub,
    trackEvent,
  ]);

  const attemptClubLaunch = useCallback(() => {
    if (isThisMonth) {
      setShowStartDialog(true);
    } else {
      launchClub();
    }
  }, [isThisMonth, launchClub]);

  const abortClubLaunch = useCallback(() => setShowStartDialog(false), []);

  if (!finishedSurvey) {
    return (
      <>
        <Text variant="heading-5" color="black">
          Is your gym currently running a Committed Club?
        </Text>

        <div className="tw-h-6 tw-pt-1 tw-pb-2">
          <Text
            variant="body-sm"
            color="wild-watermellon-600"
            className={error ? "tw-block" : "tw-hidden"}
          >
            {error}
          </Text>
        </div>

        <Radio
          selected={hasExistingClubLocal}
          options={surveyRadioOptions}
          onSelect={toggleSurveyAnswer}
        />

        <div className="tw-h-6" />

        <Button
          fullWidth
          text="Next"
          buttonType="primary"
          size="large"
          onClick={submitSurvey}
        />
      </>
    );
  }

  const dialog = (
    <DialogModal
      visible={showStartDialog}
      title="Are you ready for your Committed Club to start?"
      primaryBtnTitle="Start"
      primaryButtonOptions={{ fullWidth: true }}
      primaryHandler={launchClub}
      secondaryBtnTitle="Go Back"
      secondaryHandler={abortClubLaunch}
    >
      <p>
        {`Because you chose ${startMonthLocal?.label} which is already in progress, we will
        record member check-ins from the beginning of this month.`}
      </p>
      <br />
      <p>
        {
          "Once you start, your Committed Club will be visible in the Members App, and you can view members' progress in Core."
        }
      </p>
    </DialogModal>
  );

  return (
    <>
      {dialog}
      <Text variant="heading-5" color="black">
        Which month do you want your Committed Club to start in PushPress?
      </Text>
      <div className="tw-h-6" />

      <ReactSelect
        name="startMonth"
        options={months}
        value={startMonthLocal}
        onChange={(option: MonthOption) => setStartMonthLocal(option)}
      />

      <div className="tw-h-2" />

      <Text variant="body-lg" color="wolf-600">
        If you start mid-month, we will count all check-ins that have already
        happened in the month.
      </Text>

      {!ready && (
        <>
          <div className="tw-h-8" />
          <Text variant="body-lg" color="wild-watermellon-600">
            You must choose a name and rules before setting a start month.
          </Text>
        </>
      )}

      <div className="tw-h-6" />

      <Button
        fullWidth
        text="Save"
        size="large"
        buttonType="primary"
        disabled={!ready}
        onClick={attemptClubLaunch}
      />
    </>
  );
}

export default SetupNamePane;

const surveyRadioOptions: RadioOption[] = [
  {
    label: "Yes",
    value: true,
  },
  {
    label: "No",
    value: false,
  },
];
